import { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, X, ArrowRight, ChevronRight } from "lucide-react";
import {
  TIPOLOGIE,
  MERCATI,
  ASSOCIAZIONI,
  MERCATO_IMAGES,
  DEFAULT_IMAGE,
  TIPOLOGIA_DESCRIZIONI,
  BADGE_COLORS,
  MERCATO_COLORS,
} from "@/data/associazioniList";

export default function AssociazioniPage() {
  const [q, setQ] = useState("");
  const [tipologiaAttiva, setTipologiaAttiva] = useState(null);
  const [mercatoAttivo, setMercatoAttivo] = useState(null);
  const filterRef = useRef(null);
  const scrollToResults = () => {
    if (filterRef.current) {
      window.scrollTo({ top: filterRef.current.offsetTop - (window.innerWidth >= 1024 ? 168 : 104), behavior: "smooth" });
    }
  };

  const risultati = useMemo(() => {
    return ASSOCIAZIONI.filter((a) => {
      if (tipologiaAttiva && a.tipologia !== tipologiaAttiva) return false;
      if (mercatoAttivo && a.mercato !== mercatoAttivo) return false;
      if (q.trim()) {
        const ql = q.toLowerCase();
        if (!a.nome.toLowerCase().includes(ql) && !a.descrizione.toLowerCase().includes(ql)) return false;
      }
      return true;
    });
  }, [q, tipologiaAttiva, mercatoAttivo]);

  const hasFilters = tipologiaAttiva || mercatoAttivo || q.trim();
  const isSearching = q.trim().length > 0;

  /* Mercati disponibili per la tipologia selezionata (escludi null) */
  const mercatiDisponibili = useMemo(() => {
    const base = tipologiaAttiva
      ? ASSOCIAZIONI.filter((a) => a.tipologia === tipologiaAttiva)
      : ASSOCIAZIONI;
    const set = new Set(base.map((a) => a.mercato).filter(Boolean));
    return MERCATI.filter((m) => set.has(m));
  }, [tipologiaAttiva]);

  /* Tipologie e mercati che compaiono nei risultati correnti */
  const tipologieConRisultati = useMemo(
    () => new Set(risultati.map((a) => a.tipologia)),
    [risultati]
  );
  const mercatiConRisultati = useMemo(
    () => new Set(risultati.map((a) => a.mercato).filter(Boolean)),
    [risultati]
  );

  return (
    <main id="main-content" data-testid="associazioni-page">

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/2676587/pexels-photo-2676587.jpeg?auto=compress&cs=tinysrgb&w=1920&q=70"
          alt="Commercio Roma"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4,30,66,0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 relative">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Chi siamo</span>
            <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-white mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight leading-[1.1]">
              Associazioni, Federazioni e Gruppi
            </h1>
            <p className="text-lg text-white/80 mt-5 leading-relaxed max-w-2xl">
              Per organizzare nel modo più efficace la nostra rete di rappresentanza, riuniamo le nostre imprese in Federazioni ed Associazioni di categoria e territoriali, Gruppi trasversali e Coordinamenti specifici.
            </p>
          </div>

          {/* Search */}
          <div className="mt-10 max-w-xl">
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" aria-hidden="true" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca un'associazione..."
                data-testid="associazioni-search"
                className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/15 border border-white/25 text-white placeholder-white/50 focus:outline-none focus:border-white/60 focus:bg-white/20 transition-all text-sm"
              />
              {q && (
                <button onClick={() => setQ("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors" aria-label="Cancella ricerca">
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Filtri ──────────────────────────────────────────────────── */}
      <section ref={filterRef} className="sticky top-[104px] lg:top-[168px] z-20 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Tab Tipologia con contatori */}
          <div className="flex overflow-x-auto border-b border-[var(--confcom-border)]" style={{ scrollbarWidth: "none" }}>

            {/* Tab "Tutte" */}
            <button
              onClick={() => { setTipologiaAttiva(null); setMercatoAttivo(null); scrollToResults(); }}
              data-testid="filtro-tipologia-tutti"
              className={`shrink-0 flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 -mb-px transition-all whitespace-nowrap ${
                !tipologiaAttiva
                  ? "border-[var(--confcom-blue)] text-[var(--confcom-blue)]"
                  : "border-transparent text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-text-primary)] hover:border-gray-300"
              }`}
            >
              Tutte
              <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${!tipologiaAttiva ? "bg-[var(--confcom-blue)] text-white" : "bg-gray-100 text-gray-500"}`}>
                {isSearching ? risultati.length : ASSOCIAZIONI.length}
              </span>
            </button>

            {TIPOLOGIE.map((t) => {
              const count = isSearching
                ? risultati.filter((a) => a.tipologia === t).length
                : ASSOCIAZIONI.filter((a) => a.tipologia === t).length;
              const isExplicitlyActive = tipologiaAttiva === t;
              const hasResults = tipologieConRisultati.has(t);
              /* Active: selezionata oppure ha risultati dalla ricerca */
              const isActive = isExplicitlyActive || (isSearching && !tipologiaAttiva && hasResults);
              /* Dimmed: ricerca attiva, non selezionata, zero risultati */
              const isDimmed = isSearching && !tipologiaAttiva && !hasResults;
              const label = t;
              return (
                <button
                  key={t}
                  onClick={() => { setTipologiaAttiva(isExplicitlyActive ? null : t); setMercatoAttivo(null); scrollToResults(); }}
                  data-testid={`filtro-tipologia-${t.toLowerCase().replace(/\s+/g, "-").slice(0, 20)}`}
                  title={t}
                  className={`shrink-0 flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 -mb-px transition-all whitespace-nowrap ${
                    isActive
                      ? "border-[var(--confcom-blue)] text-[var(--confcom-blue)]"
                      : "border-transparent text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-text-primary)] hover:border-gray-300"
                  } ${isDimmed ? "opacity-35" : ""}`}
                >
                  {label}
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? "bg-[var(--confcom-blue)] text-white" : "bg-gray-100 text-gray-500"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Chip Mercato — visibili solo se la tipologia ha settori */}
          {mercatiDisponibili.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 py-3">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--confcom-text-muted)] shrink-0">Settore</span>
              <button
                onClick={() => { setMercatoAttivo(null); scrollToResults(); }}
                data-testid="filtro-mercato-tutti"
                className={`text-xs font-semibold px-3 py-1 rounded-full border transition-all ${!mercatoAttivo ? "bg-[var(--confcom-blue)] text-white border-[var(--confcom-blue)]" : "border-[var(--confcom-border)] text-[var(--confcom-text-secondary)] hover:border-[var(--confcom-blue)] hover:text-[var(--confcom-blue)]"}`}
              >
                Tutti
              </button>
              {mercatiDisponibili.map((m) => {
                const count = isSearching
                  ? risultati.filter((a) => a.mercato === m && (!tipologiaAttiva || a.tipologia === tipologiaAttiva)).length
                  : ASSOCIAZIONI.filter((a) => a.mercato === m && (!tipologiaAttiva || a.tipologia === tipologiaAttiva)).length;
                const isExplicitlyActive = mercatoAttivo === m;
                const hasResults = mercatiConRisultati.has(m);
                const isActive = isExplicitlyActive || (isSearching && !mercatoAttivo && hasResults);
                const isDimmed = isSearching && !mercatoAttivo && !hasResults;
                const mc = MERCATO_COLORS[m] || { chip: "border-[var(--confcom-border)] text-[var(--confcom-text-secondary)]", active: "bg-[var(--confcom-blue)] text-white border-[var(--confcom-blue)]", badge: "" };
                return (
                  <button
                    key={m}
                    onClick={() => { setMercatoAttivo(isExplicitlyActive ? null : m); scrollToResults(); }}
                    data-testid={`filtro-mercato-${m.toLowerCase().replace(/\s+/g, "-").slice(0, 20)}`}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border transition-all ${
                      isActive ? mc.active : mc.chip
                    } ${isDimmed ? "opacity-35" : ""}`}
                  >
                    {m}
                    <span className={`text-[10px] font-bold px-1 py-0.5 rounded-full leading-none ${isActive ? "bg-white/25 text-white" : "bg-white/60"}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

        </div>
      </section>

      {/* ── Descrizione categoria attiva ─────────────────────────────── */}
      {tipologiaAttiva && TIPOLOGIA_DESCRIZIONI[tipologiaAttiva] && (
        <div className="bg-[#EEF4FB] border-b border-[#C8DDEF]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <p className="text-xs text-[var(--confcom-text-primary)] leading-relaxed max-w-4xl">
              <strong className="font-bold text-[var(--confcom-blue)]">{tipologiaAttiva} — </strong>
              {TIPOLOGIA_DESCRIZIONI[tipologiaAttiva]}
            </p>
          </div>
        </div>
      )}

      {/* ── Risultati ───────────────────────────────────────────────── */}
      <section className="py-12 bg-[#F8FAFD]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Counter + reset */}
          <div className="flex items-center justify-between mb-8">
            <p className="text-sm text-[var(--confcom-text-muted)]">
              <span className="font-bold text-[var(--confcom-text-primary)]">{risultati.length}</span> associazion{risultati.length === 1 ? "e" : "i"} trovat{risultati.length === 1 ? "a" : "e"}
            </p>
            {hasFilters && (
              <button
                onClick={() => { setQ(""); setTipologiaAttiva(null); setMercatoAttivo(null); }}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--confcom-blue)] hover:underline"
                data-testid="reset-filtri"
              >
                <X size={12} /> Rimuovi filtri
              </button>
            )}
          </div>

          {risultati.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[var(--confcom-text-muted)] mb-2">Nessuna associazione trovata.</p>
              <button onClick={() => { setQ(""); setTipologiaAttiva(null); setMercatoAttivo(null); }} className="text-sm text-[var(--confcom-blue)] hover:underline">Reimposta i filtri</button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {risultati.map((a) => (
                <Link
                  key={a.id}
                  to={`/associazioni/${a.id}`}
                  data-testid={`associazione-card-${a.id}`}
                  className="group flex flex-col bg-white rounded-xl border border-[var(--confcom-border)] overflow-hidden hover:border-[var(--confcom-blue)]/40 hover:shadow-md transition-all"
                >
                  {/* Foto copertina / Logo */}
                  {a.logo ? (
                    <div className="relative h-60 overflow-hidden bg-white flex items-center justify-center p-2">
                      <img
                        src={a.logo}
                        alt={`Logo ${a.nome}`}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={MERCATO_IMAGES[a.mercato] || DEFAULT_IMAGE}
                        alt={a.nome}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {/* Overlay sottile */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                      {/* Badge mercato in sovrimpressione */}
                      {a.mercato && (
                        <span className="absolute bottom-2 left-3 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-white/90 text-[var(--confcom-blue)]">
                          {a.mercato}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Contenuto */}
                  <div className="flex flex-col flex-1 p-5">
                    {/* Badge tipologia + mercato */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${BADGE_COLORS[a.tipologia]}`}>
                        {a.tipologia}
                      </span>
                      {a.mercato && (
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${(MERCATO_COLORS[a.mercato] || {}).badge || "bg-gray-100 text-gray-600"}`}>
                          {a.mercato}
                        </span>
                      )}
                    </div>

                    {/* Nome */}
                    <h3 className="text-sm font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] leading-snug mb-2 group-hover:text-[var(--confcom-blue)] transition-colors flex-1">
                      {a.nome}
                    </h3>

                    {/* Descrizione */}
                    <p className="text-xs text-[var(--confcom-text-secondary)] leading-relaxed line-clamp-2 mb-4">
                      {a.descrizione}
                    </p>

                    {/* Link */}
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--confcom-blue)]">
                      Scopri di più <ChevronRight size={13} aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────── */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/cta-background.jpg" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 59, 117, 0.88)" }} />
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight mb-4">
            Non trovi la tua categoria?
          </h2>
          <p className="text-white/80 mb-2">
            Puoi comunque entrare a far parte di Confcommercio Roma.
          </p>
          <p className="text-white/70 text-sm mb-8">
            Email: <a href="mailto:associati@confcommercioroma.it" className="underline hover:text-white">associati@confcommercioroma.it</a>
            &nbsp;&nbsp;|&nbsp;&nbsp;
            Tel: <a href="tel:0668437500" className="underline hover:text-white">06.68.437.500 / 235 / 755</a>
          </p>
          <Link
            to="/contatti"
            className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-8 py-4 rounded-md font-semibold hover:opacity-90 transition-opacity shadow-lg"
          >
            Contattaci <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </section>

    </main>
  );
}
